import React, { FC, useEffect, useMemo } from 'react';

import Typography from '@mui/material/Typography';
import * as Sentry from '@sentry/react';
import { TFunction } from 'i18next';
import qs from 'qs';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import ErrorIcon from '../../assets/icons/ErrorIcon';
import { IKImage } from '../../components/IKImage';
import FullScreenBackgroundWrap from '../../components/UI/FullScreenBackgroudWrap';
import styled from '../../styled';
import {
  BIG_MARGIN_PX,
  colorsPalette,
  LARGE_MARGIN_PX,
  MIDDLE_MARGIN_PX,
  SMALL_MARGIN_PX,
} from '../../theme';
import Auth0Error from '../../utils/errors/Auth0Error';

const backgroundUrl = '/page-images/auth0ErrorPageBg.png';
const fuLogo = '/Finance-unlocked-logo.png';
const suLogo = 'sustainabilityunlocked.png';
const elodLogo = '/auth/elodLogo.png';

const logoTransformation = [
  {
    height: 40,
  },
];

const updatedErrors = [
  {
    tKey: 'resetPasswordLinkUsed',
    originalValue: 'This URL can be used only once',
    shouldSentSentryError: false,
  },
  {
    tKey: 'linkExpired',
    originalValue: 'Access expired.',
    shouldSentSentryError: false,
  },
];

const checkOnTextUpdates = (text: string | null | undefined, t: TFunction) => {
  if (text) {
    const match = updatedErrors.find((item) => item.originalValue === text);

    if (match) {
      return [t(`error.${match.tKey}`), match.shouldSentSentryError];
    }
  }

  return [t('error.defaultAuthErrorDescription'), true];
};

const AuthErrorPage: FC = () => {
  const location = useLocation();
  const { t } = useTranslation();

  const [errorMessage, shouldSendError] = useMemo(() => {
    const parsed = qs.parse(location?.search?.slice(1))?.error_description as string | undefined;
    return checkOnTextUpdates(parsed, t);
  }, [location, t]);

  useEffect(() => {
    if (shouldSendError && process.env.NODE_ENV === 'production') {
      const parsed = qs.parse(location?.search?.slice(1))?.error_description || '';
      Sentry.captureException(new Auth0Error(parsed));
    }
  }, [shouldSendError, location]);

  return (
    <FullScreenBackgroundWrap backgroundUrl={backgroundUrl}>
      <Wrapper>
        <TopWrapper>
          <Heading>
            <StyledIcon />
            <Header variant="h1">{t('error.error')}</Header>
          </Heading>
          <ErrorDescription>{errorMessage}</ErrorDescription>
        </TopWrapper>
        <InstructionText>{t('page.auth0Error.instruction')}</InstructionText>
        <LogosWrapper>
          <LogoImgWrapper>
            <a href={process.env.REACT_APP_FU_SITE_LINK}>
              <LogoImg path={fuLogo} transformation={logoTransformation} />
            </a>
          </LogoImgWrapper>
          <LogoImgWrapper>
            <a href={process.env.REACT_APP_ELOD_SITE_LINK}>
              <LogoImg path={elodLogo} transformation={logoTransformation} />
            </a>
          </LogoImgWrapper>
          <LogoImgWrapper>
            <a href={process.env.REACT_APP_SU_SITE_LINK}>
              <LogoImg path={suLogo} transformation={logoTransformation} />
            </a>
          </LogoImgWrapper>
        </LogosWrapper>
      </Wrapper>
    </FullScreenBackgroundWrap>
  );
};

export default AuthErrorPage;

const Wrapper = styled('div')`
  padding: 0 16px;
`;

const TopWrapper = styled('div')`
  max-width: 480px;
  margin-bottom: ${LARGE_MARGIN_PX};
  margin-left: auto;
  margin-right: auto;
  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-bottom: ${BIG_MARGIN_PX};
  }
`;

const Heading = styled('div')`
  margin-bottom: ${SMALL_MARGIN_PX};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledIcon = styled(ErrorIcon)`
  width: 40px;
  height: 40px;
  margin-right: ${SMALL_MARGIN_PX};
  ${({ theme }) => theme.breakpoints.up('md')} {
    width: 48px;
    height: 48px;
  }
`;

const Header = styled(Typography)`
  color: ${colorsPalette.coral};
  font-weight: bold;
  font-size: 32px;
  line-height: 38px;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    font-size: 32px;
    line-height: 38px;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 40px;
    line-height: 48px;
  }
`;

export const ErrorDescription = styled(Typography)`
  font-size: 18px;
  line-height: 24px;
  text-align: center;
  color: ${colorsPalette.white};
  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 20px;
    line-height: 30px;
  }
`;

const InstructionText = styled(Typography)`
  text-align: center;
  color: ${colorsPalette.gray};
  font-size: 16px;
  line-height: 24px;
  margin-bottom: ${SMALL_MARGIN_PX};
  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 18px;
    line-height: 28px;
  }
`;

const LogosWrapper = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 300px;
  margin: 0 auto;
  ${({ theme }) => theme.breakpoints.up('md')} {
    flex-direction: row;
  }
`;

const LogoImgWrapper = styled('div')`
  position: relative;
  padding: ${MIDDLE_MARGIN_PX};
  display: flex;
  justify-content: center;
  width: 100%;
  &::after {
    content: '';
    display: block;
    width: 100%;
    height: 1px;
    background: ${colorsPalette.white};
    opacity: 0.33;
    position: absolute;
    right: 0;
    bottom: 0;
  }
  &:last-child::after {
    display: none;
  }
  ${({ theme }) => theme.breakpoints.up('md')} {
    padding: 0 ${MIDDLE_MARGIN_PX};
    &::after {
      width: 1px;
      height: 100%;
      top: 0;
    }
  }
`;

const LogoImg = styled(IKImage)`
  height: 40px;
`;
